import { AssessmentResults } from './AssessmentResults';
import { RecruitingPeriodAnalytics } from './RecruitingWorkflows';
// pages/hr/recruiting/CampaignDetailPage.tsx
import React from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  Chip,
  Grid,
  Stack,
  Divider,
  LinearProgress,
  IconButton,
} from '@mui/joy';
import {
  ArrowLeft as ArrowBackOutlined,
  Play as PlayArrowOutlined,
  Pause as PauseOutlined,
  PencilSimple as EditOutlined,
  Users as PeopleOutlined,
  TrendUp as TrendingUpOutlined,
  CheckCircle as CheckCircleOutlined,
  Clock as ScheduleOutlined,
  ClipboardText as AssignmentOutlined,
  Copy as ContentCopyOutlined,
} from '@phosphor-icons/react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useRecruitingCampaign,
  useCampaignStats,
  useLaunchCampaign,
  usePauseCampaign,
} from '../../../hooks/useHr';
import type { CampaignStatus } from '../../../types/hr';
import toast from 'react-hot-toast';

const statusColors: Record<CampaignStatus, 'success' | 'warning' | 'danger' | 'neutral'> = {
  draft: 'neutral',
  active: 'success',
  paused: 'warning',
  closed: 'danger',
};

const statusLabels: Record<CampaignStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  paused: 'Paused',
  closed: 'Closed',
};

export const CampaignDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { data: campaign, isLoading } = useRecruitingCampaign(id || '');
  const { data: stats } = useCampaignStats(id || '');
  const launchCampaign = useLaunchCampaign();
  const pauseCampaign = usePauseCampaign();

  const handleLaunch = async () => {
    if (!id) return;
    try {
      await launchCampaign.mutateAsync(id);
      toast.success('Campaign launched');
    } catch {
      toast.error('Failed to launch campaign');
    }
  };

  const handlePause = async () => {
    if (!id) return;
    try {
      await pauseCampaign.mutateAsync(id);
      toast.success('Campaign paused');
    } catch {
      toast.error('Failed to pause campaign');
    }
  };

  const copyApplyLink = () => {
    if (!campaign) return;
    const link = `${window.location.origin}/apply/${campaign.slug}`;
    navigator.clipboard.writeText(link);
    toast.success('Apply link copied to clipboard');
  };

  if (isLoading || !campaign) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  const hiringProgress = stats
    ? Math.min((stats.hired_this_month / stats.hiring_goal) * 100, 100)
    : 0;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/hr/recruiting')}>
          <ArrowBackOutlined />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography level="h2">{campaign.name}</Typography>
            <Chip
              size="sm"
              variant="soft"
              color={statusColors[campaign.status]}
            >
              {statusLabels[campaign.status]}
            </Chip>
          </Box>
          <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
            {campaign.role_title} {campaign.location && `• ${campaign.location}`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {campaign.status === 'draft' && (
            <Button
              startDecorator={<PlayArrowOutlined />}
              onClick={handleLaunch}
              loading={launchCampaign.isPending}
            >
              Launch
            </Button>
          )}
          {campaign.status === 'active' && (
            <Button
              variant="outlined"
              color="primary"
              startDecorator={<PauseOutlined />}
              onClick={handlePause}
              loading={pauseCampaign.isPending}
            >
              Pause
            </Button>
          )}
          {campaign.status === 'paused' && (
            <Button
              startDecorator={<PlayArrowOutlined />}
              onClick={handleLaunch}
              loading={launchCampaign.isPending}
            >
              Resume
            </Button>
          )}
          <Button
            variant="outlined"
            color="primary"
            startDecorator={<EditOutlined />}
            onClick={() => navigate(`/hr/recruiting/${id}/edit`)}
          >
            Edit
          </Button>
          <Button
            startDecorator={<TrendingUpOutlined />}
            onClick={() => navigate(`/hr/recruiting/${id}/pipeline`)}
          >
            View Pipeline
          </Button>
        </Box>
      </Box>

      {id && <Card sx={{mt: 3}}><AssessmentResults campaignId={id} /><Button variant="outlined" onClick={() => navigate(`/hr/recruiting/${id}/edit?tab=assessment`)}>Assessment setup</Button></Card>}
      <RecruitingPeriodAnalytics campaignId={campaign.id} />
      {/* Apply Link */}
      {campaign.status === 'active' && (
        <Card sx={{ mb: 3, p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography level="title-sm">Public Apply Link</Typography>
              <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                {window.location.origin}/apply/{campaign.slug}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="primary"
              size="sm"
              startDecorator={<ContentCopyOutlined />}
              onClick={copyApplyLink}
            >
              Copy Link
            </Button>
          </Box>
        </Card>
      )}

      <Grid container spacing={3}>
        {/* Stats Cards */}
        <Grid xs={12} md={8}>
          <Grid container spacing={2}>
            <Grid xs={6} sm={3}>
              <Card>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <PeopleOutlined size={32} color="var(--joy-palette-primary-500)" style={{ marginBottom: 8 }} />
                  <Typography level="h3">{stats?.total_candidates || 0}</Typography>
                  <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                    Total Candidates
                  </Typography>
                </Box>
              </Card>
            </Grid>
            <Grid xs={6} sm={3}>
              <Card>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <AssignmentOutlined size={32} color="var(--joy-palette-warning-500)" style={{ marginBottom: 8 }} />
                  <Typography level="h3">{stats?.funnel.qualified || 0}</Typography>
                  <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                    Qualified
                  </Typography>
                </Box>
              </Card>
            </Grid>
            <Grid xs={6} sm={3}>
              <Card>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <ScheduleOutlined size={32} color="var(--joy-palette-primary-500)" style={{ marginBottom: 8 }} />
                  <Typography level="h3">
                    {(stats?.funnel.screening_scheduled || 0) + (stats?.funnel.interview_scheduled || 0)}
                  </Typography>
                  <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                    Scheduled
                  </Typography>
                </Box>
              </Card>
            </Grid>
            <Grid xs={6} sm={3}>
              <Card>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <CheckCircleOutlined size={32} color="var(--joy-palette-success-500)" style={{ marginBottom: 8 }} />
                  <Typography level="h3">{stats?.funnel.hired || 0}</Typography>
                  <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                    Hired
                  </Typography>
                </Box>
              </Card>
            </Grid>
          </Grid>

          {/* Funnel Visualization */}
          <Card sx={{ mt: 2 }}>
            <Typography level="title-lg" sx={{ mb: 2 }}>Recruitment Funnel</Typography>
            <Stack spacing={2}>
              {[
                { label: 'Applied', value: stats?.funnel.applied || 0, color: 'neutral' },
                { label: 'Qualified', value: stats?.funnel.qualified || 0, color: 'warning' },
                { label: 'Screening Scheduled', value: stats?.funnel.screening_scheduled || 0, color: 'primary' },
                { label: 'Screening Completed', value: stats?.funnel.screening_completed || 0, color: 'primary' },
                { label: 'Interview Scheduled', value: stats?.funnel.interview_scheduled || 0, color: 'primary' },
                { label: 'Offer', value: stats?.funnel.offer || 0, color: 'success' },
                { label: 'Hired', value: stats?.funnel.hired || 0, color: 'success' },
              ].map((stage, index) => {
                const maxValue = stats?.total_candidates || 1;
                const percentage = (stage.value / maxValue) * 100;
                return (
                  <Box key={stage.label}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography level="body-sm">{stage.label}</Typography>
                      <Typography level="body-sm" fontWeight="lg">
                        {stage.value}
                      </Typography>
                    </Box>
                    <LinearProgress
                      determinate
                      value={percentage}
                      color={stage.color as 'primary' | 'warning' | 'success' | 'neutral'}
                      sx={{ height: 8 }}
                    />
                  </Box>
                );
              })}
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', gap: 4 }}>
              <Box>
                <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                  Applied → Qualified
                </Typography>
                <Typography level="h4">
                  {stats?.conversion_rates.applied_to_qualified.toFixed(1)}%
                </Typography>
              </Box>
              <Box>
                <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                  Qualified → Scheduled
                </Typography>
                <Typography level="h4">
                  {stats?.conversion_rates.qualified_to_scheduled.toFixed(1)}%
                </Typography>
              </Box>
              <Box>
                <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                  Rejected
                </Typography>
                <Typography level="h4" color="danger">
                  {stats?.rejected || 0}
                </Typography>
              </Box>
              <Box>
                <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                  Withdrawn
                </Typography>
                <Typography level="h4" sx={{ color: 'neutral.500' }}>
                  {stats?.withdrawn || 0}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid xs={12} md={4}>
          {/* Hiring Progress */}
          <Card sx={{ mb: 2 }}>
            <Typography level="title-lg" sx={{ mb: 2 }}>Monthly Progress</Typography>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography level="body-sm">
                  {stats?.hired_this_month || 0} of {stats?.hiring_goal || campaign.hiring_goal_per_month} hired
                </Typography>
                <Typography level="body-sm" fontWeight="lg">
                  {hiringProgress.toFixed(0)}%
                </Typography>
              </Box>
              <LinearProgress
                determinate
                value={hiringProgress}
                color={hiringProgress >= 100 ? 'success' : 'primary'}
                sx={{ height: 12 }}
              />
            </Box>
          </Card>

          {/* Campaign Details */}
          <Card>
            <Typography level="title-lg" sx={{ mb: 2 }}>Campaign Details</Typography>
            <Stack spacing={2}>
              <Box>
                <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                  Role Title
                </Typography>
                <Typography level="body-md">{campaign.role_title}</Typography>
              </Box>
              {campaign.location && (
                <Box>
                  <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                    Location
                  </Typography>
                  <Typography level="body-md">{campaign.location}</Typography>
                </Box>
              )}
              {campaign.description && (
                <Box>
                  <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                    Description
                  </Typography>
                  <Typography level="body-md">{campaign.description}</Typography>
                </Box>
              )}
              <Box>
                <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                  Monthly Hiring Goal
                </Typography>
                <Typography level="body-md">{campaign.hiring_goal_per_month}</Typography>
              </Box>
              <Box>
                <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                  Qualification Questions
                </Typography>
                {campaign.qualification_questions?.length ? (
                  <Typography level="body-md">
                    {campaign.qualification_questions.length} questions
                  </Typography>
                ) : (
                  <Typography level="body-md" color="warning">
                    None — candidates are never screened automatically
                  </Typography>
                )}
              </Box>
              <Box>
                <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                  Scheduling Team
                </Typography>
                {campaign.scheduling_team_name ? (
                  <Typography level="body-md">{campaign.scheduling_team_name}</Typography>
                ) : (
                  <Typography level="body-md" color="warning">
                    Not set — scheduling links cannot be sent
                  </Typography>
                )}
              </Box>
              <Box>
                <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                  Created
                </Typography>
                <Typography level="body-md">
                  {new Date(campaign.created_at).toLocaleDateString()}
                  {campaign.created_by_name && ` by ${campaign.created_by_name}`}
                </Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CampaignDetailPage;
